import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChatText, Phone, Stack, Storefront } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { loadCompany } from "@/lib/company";
import { smsHref, viewingText } from "@/lib/sms";

const iso = (d) => d.toISOString().slice(0, 10);
const prettyTime = (t) => {
  if (!t) return "Any time";
  const [h, m] = t.split(":").map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

// Desk tick: the slab is pulled and on the rack before the shopper walks in.
export const PulledToggle = ({ v, onChange, size = "sm" }) => {
  const [busy, setBusy] = useState(false);
  const flip = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/viewings/${v.id}/pulled`, { pulled: !v.pulled });
      onChange(data);
      toast.success(data.pulled ? "Slab pulled — on the rack" : "Marked not pulled yet");
    } catch {
      toast.error("Could not update that viewing");
    }
    setBusy(false);
  };
  const big = size === "lg";
  return (
    <button
      type="button"
      onClick={flip}
      disabled={busy}
      aria-pressed={v.pulled}
      data-testid={`viewing-pulled-toggle-${v.id}`}
      title={
        v.pulled
          ? `Pulled ${v.pulled_at?.slice(0, 16).replace("T", " ") || ""}${v.pulled_by ? ` by ${v.pulled_by}` : ""}`
          : "Tick when the slab is pulled and on the rack"
      }
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border font-bold uppercase tracking-widest transition-colors duration-150 ${
        big ? "h-11 px-4 text-xs" : "h-7 px-2.5 text-[10px]"
      } ${
        v.pulled
          ? "border-emerald-400/60 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25"
          : "border-zinc-700 text-zinc-400 hover:border-cyan-400/60 hover:text-cyan-200"
      }`}
    >
      <Stack size={big ? 15 : 12} weight="bold" />
      {v.pulled ? "Slab pulled" : "Pull slab"}
    </button>
  );
};

// Today's and tomorrow's booked showroom viewings, so the desk has the slab pulled and someone ready.
export const UpcomingViewings = () => {
  const [items, setItems] = useState(null);
  const [shop, setShop] = useState("");
  const today = iso(new Date());
  const tomorrow = iso(new Date(Date.now() + 86400000));
  const replace = (next) =>
    setItems((list) => (list || []).map((x) => (x.id === next.id ? next : x)));

  useEffect(() => {
    api
      .get("/viewings", { params: { upcoming: true } })
      .then(({ data }) =>
        setItems(
          (data.items || []).filter(
            (v) => v.status === "Booked" && (v.date === today || v.date === tomorrow),
          ),
        ),
      )
      .catch(() => setItems([]));
    loadCompany().then((c) => setShop(c?.name || ""));
  }, [today, tomorrow]);

  const list = items || [];
  const pulledCount = list.filter((v) => v.pulled).length;
  return (
    <div
      className="mt-6 rounded-lg border border-zinc-800 bg-zinc-800/40 p-4"
      data-testid="upcoming-viewings"
    >
      <div className="flex items-center gap-2">
        <Storefront size={16} weight="bold" className="text-cyan-300" />
        <div className="text-xs uppercase tracking-widest text-zinc-500">
          Showroom viewings · today &amp; tomorrow
        </div>
        {list.length > 0 && (
          <span
            className={`hidden rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest sm:inline ${
              pulledCount === list.length
                ? "border-emerald-400/40 text-emerald-300"
                : "border-amber-400/40 text-amber-300"
            }`}
            data-testid="upcoming-viewings-pulled-summary"
          >
            {pulledCount}/{list.length} slabs pulled
          </span>
        )}
        <Link
          to="/calendar"
          data-testid="upcoming-viewings-calendar"
          className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          Calendar
        </Link>
        <span
          className="font-mono-data text-sm font-bold text-cyan-300"
          data-testid="upcoming-viewings-count"
        >
          {list.length}
        </span>
      </div>
      {items === null ? (
        <div className="mt-3 h-10 animate-pulse rounded-md bg-zinc-800" />
      ) : list.length === 0 ? (
        <p className="mt-3 text-sm text-zinc-500" data-testid="upcoming-viewings-empty">
          No viewings booked for today or tomorrow.
        </p>
      ) : (
        <div className="mt-3 grid gap-2 lg:grid-cols-2">
          {list.map((v) => (
            <div
              key={v.id}
              data-testid={`upcoming-viewing-${v.id}`}
              className={`flex items-center gap-3 rounded-md border p-3 ${
                v.pulled
                  ? "border-emerald-500/25 bg-emerald-500/5"
                  : "border-cyan-500/25 bg-cyan-500/5"
              }`}
            >
              <Link to={`/viewings/${v.id}`} className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                      v.date === today
                        ? "border-cyan-400/50 text-cyan-200"
                        : "border-zinc-700 text-zinc-400"
                    }`}
                  >
                    {v.date === today ? "Today" : "Tomorrow"}
                  </span>
                  <span className="font-mono-data text-xs text-cyan-200">{prettyTime(v.time)}</span>
                  <span className="truncate text-sm font-semibold">{v.customer_name}</span>
                </div>
                <div className="truncate text-xs text-zinc-400">
                  {v.item || "A slab"}
                  {v.where ? ` · ${v.where}` : ""}
                  {v.assignee ? ` · with ${v.assignee}` : ""}
                </div>
              </Link>
              <PulledToggle v={v} onChange={replace} />
              {v.phone && (
                <a
                  href={smsHref(v.phone, viewingText(v.customer_name, shop, v.item, v.date, v.time, v.where))}
                  data-testid={`upcoming-viewing-text-${v.id}`}
                  className="shrink-0 text-zinc-400 hover:text-cyan-300"
                  title={`Text ${v.customer_name}`}
                >
                  <ChatText size={16} weight="bold" />
                </a>
              )}
              {v.phone && (
                <a
                  href={`tel:${v.phone.replace(/[^\d+]/g, "")}`}
                  data-testid={`upcoming-viewing-call-${v.id}`}
                  className="shrink-0 text-zinc-400 hover:text-yellow-400"
                  title={v.phone}
                >
                  <Phone size={16} weight="bold" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UpcomingViewings;
